"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { getCurrentUser, getGetCurrentUserQueryKey, logoutUser } from "@/app/generated/api/auth/auth";
import type { CurrentUserResponse, WorkspaceDto } from "@/app/generated/api/schemas";
import { User, Workspace } from "@/app/components/workspace/api";
import { setAdminLocaleCookie, preferredLocaleFromUser } from "@/i18n/client";
import { WorkspaceRouter } from "../../workspace/router";
import { isWorkspaceSelectionPath, workspaceUuidFromPath } from "../../workspace/routeContext";
import { completeFrontendLogout } from "../../workspace/sessionLogout";
import { clearPartnerReferral } from "@/app/modules/partners/referral/usePartnerReferral";
import { withoutPartnerReferralParams } from "@/app/modules/partners/referral/storage";
import { hrefWithCurrentIndustryPresetIntent } from "@/app/lib/industryPresetIntent";
import { clearAiServiceSession } from "@/app/lib/aiServiceSession";
import { useCurrentUserSession } from "@/app/modules/auth/hooks/useCurrentUserSession";

type SessionResponse = Omit<CurrentUserResponse, "workspaces"> & {
  user: User;
  workspaces: Workspace[];
};

function normalizeSession(data: CurrentUserResponse): SessionResponse {
  return {
    ...data,
    user: data.user,
    workspaces: Array.isArray(data.workspaces) ? data.workspaces as WorkspaceDto[] : [],
  };
}

function replaceOrPush(router: WorkspaceRouter, href: string): void {
  if (router.replace) {
    router.replace(href);
    return;
  }

  router.push(href);
}

export function useAccountSession(router: WorkspaceRouter) {
  const tCommon = useTranslations("common");
  const tAuth = useTranslations("workspaceShell.auth");
  const tPartner = useTranslations("partners.pages.account");
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const urlWorkspaceUuid = useMemo(() => workspaceUuidFromPath(pathname), [pathname]);
  const [activeWorkspaceUuid, setActiveWorkspaceUuid] = useState(urlWorkspaceUuid);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const runningRef = useRef(false);

  const sessionQuery = useCurrentUserSession();

  const user = sessionQuery.data?.user ?? null;
  const workspaces = useMemo(() => sessionQuery.data ? normalizeSession(sessionQuery.data).workspaces : [], [sessionQuery.data]);
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.uuid === activeWorkspaceUuid) ?? null,
    [activeWorkspaceUuid, workspaces],
  );

  useEffect(() => {
    if (user) setAdminLocaleCookie(preferredLocaleFromUser(user));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const url = new URL(window.location.href);
    const cleanPath = withoutPartnerReferralParams(url);
    clearPartnerReferral();
    if (cleanPath === `${url.pathname}${url.search}${url.hash}`) return;

    window.history.replaceState(window.history.state, "", `${url.origin}${cleanPath}`);
  }, [user, pathname]);

  useEffect(() => {
    setActiveWorkspaceUuid((current) => (urlWorkspaceUuid === current ? current : urlWorkspaceUuid));
  }, [urlWorkspaceUuid]);

  useEffect(() => {
    if (!sessionQuery.data) return;

    const nextUuid = activeWorkspaceUuid || workspaceUuidFromPath(window.location.pathname);
    setActiveWorkspaceUuid(
      nextUuid && workspaces.some((workspace) => workspace.uuid === nextUuid) ? nextUuid : "",
    );
  }, [activeWorkspaceUuid, sessionQuery.data, workspaces]);

  useEffect(() => {
    if (!sessionQuery.data || pathname !== "/c") return;

    replaceOrPush(router, hrefWithCurrentIndustryPresetIntent("/"));
  }, [pathname, router, sessionQuery.data]);

  useEffect(() => {
    setError(null);
    setMessage(null);
  }, [pathname]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    if (!oauthError) return;

    setError(oauthError === "invalid_referral_code" ? tPartner("invalidReferralCode") : tAuth("oauthError"));
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, [tAuth, tPartner]);

  async function reload(preferredWorkspaceUuid?: string) {
    const data = normalizeSession(await getCurrentUser());
    setAdminLocaleCookie(preferredLocaleFromUser(data.user));
    queryClient.setQueryData(getGetCurrentUserQueryKey(), data);

    const preferredUuid = preferredWorkspaceUuid || workspaceUuidFromPath(window.location.pathname);
    const nextUuid = preferredUuid && data.workspaces.some((workspace) => workspace.uuid === preferredUuid)
      ? preferredUuid
      : "";

    setActiveWorkspaceUuid(isWorkspaceSelectionPath(window.location.pathname) ? "" : nextUuid);

    if (window.location.pathname === "/c") {
      replaceOrPush(router, hrefWithCurrentIndustryPresetIntent("/"));
    }
  }

  async function run(action: () => Promise<void>, success?: string) {
    if (runningRef.current) {
      return;
    }

    runningRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
      if (success) setMessage(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : tCommon("requestFailed"));
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  async function logout() {
    await run(async () => {
      await completeFrontendLogout(
        queryClient,
        getGetCurrentUserQueryKey(),
        logoutUser,
      );
      clearAiServiceSession();
      setActiveWorkspaceUuid("");
      replaceOrPush(router, "/");
    });
  }

  return {
    user,
    workspaces,
    activeWorkspaceUuid,
    activeWorkspace,
    loading: sessionQuery.isLoading,
    busy,
    error,
    message,
    reload,
    run,
    logout,
    setActiveWorkspaceUuid,
  };
}
